import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ByzMuiOpenProcessMenu from 'src/components/core-components/mui/ByzMuiOpenProcessMenu';
import ByzEditButton from 'src/components/core-components/mui/buttons-grid/ByzGrdEditButton';
import ByzCard from 'src/components/core-components/ByzCard';
import { useTranslate } from 'src/locales';
import { IsolationRow } from '../models';

type IsolationRequestListProps = {
  rows: IsolationRow[];
  getImageSrc: (base64: string | null | undefined) => string;
  onRowActionClick: (row: IsolationRow) => void;
  onEditClick: (row: IsolationRow) => void;
};

const IsolationRequestList: React.FC<IsolationRequestListProps> = ({
  rows,
  getImageSrc,
  onRowActionClick,
  onEditClick,
}) => {
  const { t } = useTranslate();

  return (
    <ByzCard title={t('isolation-requests.isolation-requests')} sx={{ mt: 2 }}>
      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead
            sx={{
              '& th': {
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 700,
                fontSize: '12px',
                lineHeight: '24px',
                letterSpacing: '0.17px',
                backgroundColor: '#F4F7FE',
                color: '#000000',
                border: '1px solid #E0E0E0',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                py: 1,
                px: 2,
              },
            }}
          >
            <TableRow>
              <TableCell />
              <TableCell>{t('isolation-requests.grid-columns.icon')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.name')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.take-reason')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.take-description')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.non-bacterial-reason')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.non-bacterial-description')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.microbiology')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.kitchen')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.cleaning')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.start-date')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.end-date')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.patient-and-family')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.approval-time')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.status')}</TableCell>
              <TableCell>{t('isolation-requests.grid-columns.edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((item) => (
              <TableRow key={item.id}>
                <TableCell
                  sx={{
                    width: 60,
                    borderRight: '1px solid #E5E7EB',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ByzMuiOpenProcessMenu
                      menuItems={[]}
                      onlyDesign
                      onOpen={() => onRowActionClick(item)}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  {item.icon && (
                    <img
                      src={getImageSrc(item.icon)}
                      alt={item.isolationtypename}
                      style={{ width: 24, height: 24 }}
                    />
                  )}
                </TableCell>
                <TableCell>{item.isolationtypename}</TableCell>
                <TableCell>{item.isolation_taken_reason_label}</TableCell>
                <TableCell>{item.isolation_taken_descrption}</TableCell>
                <TableCell>{item.isolation_without_bacteria_label}</TableCell>
                <TableCell>{item.isolation_without_bact_descrp}</TableCell>
                <TableCell>
                  {item.notification_lab === '1'
                    ? t('isolation-requests.common-yes')
                    : t('isolation-requests.common-no')}
                </TableCell>
                <TableCell>
                  {item.notification_kitchen === '1'
                    ? t('isolation-requests.common-yes')
                    : t('isolation-requests.common-no')}
                </TableCell>
                <TableCell>
                  {item.notification_cleaner === '1'
                    ? t('isolation-requests.common-yes')
                    : t('isolation-requests.common-no')}
                </TableCell>
                <TableCell>
                  {item.start_date
                    ? new Date(item.start_date).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell>
                  {item.finish_date
                    ? new Date(item.finish_date).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell>
                  {item.is_given_family_education === '1'
                    ? t('isolation-requests.common-yes')
                    : t('isolation-requests.common-no')}
                </TableCell>
                <TableCell>
                  {item.app_or_reject_date
                    ? new Date(item.app_or_reject_date).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor:
                      item.approve_status === '0'
                        ? '#FFCC004D'
                        : item.approve_status === '1'
                          ? '#AEADA64D'
                          : item.approve_status === '2'
                            ? '#34C7594D'
                            : 'transparent',
                    fontWeight: 700,
                  }}
                >
                  {item.approve_status === '0'
                    ? t('isolation-requests.status-labels.pending')
                    : item.approve_status === '1'
                      ? t('isolation-requests.status-labels.sent')
                      : item.approve_status === '2'
                        ? t('isolation-requests.status-labels.approved')
                        : item.approve_status === '3'
                          ? t('isolation-requests.status-labels.cancelled')
                          : item.approve_status === '4'
                            ? t('isolation-requests.status-labels.completed')
                            : ''}
                </TableCell>
                <TableCell>
                  <ByzEditButton
                    size="small"
                    onClick={() => onEditClick(item)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={16} align="center" />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </ByzCard>
  );
};

export default IsolationRequestList;

